import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
}

export function useOnboarding() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [profileResult, roleResult] = await Promise.all([
        supabase.from('profiles').select('onboarding_completed').eq('id', user.id).single(),
        supabase.rpc('get_user_role', { _user_id: user.id })
      ]);
      
      const profile = profileResult.data;
      const userRole = roleResult.data;

      if (profile && userRole) {
        setIsOnboardingComplete(profile.onboarding_completed || false);
        if (!profile.onboarding_completed) {
          initializeSteps(userRole);
        }
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const initializeSteps = (role: string) => {
    let onboardingSteps: OnboardingStep[] = [];

    if (role === 'buyer') {
      onboardingSteps = [
        { id: 'welcome', title: 'Welcome to SabiTradeNG', description: 'Let\'s get you started with secure trading', completed: false },
        { id: 'profile', title: 'Complete Your Profile', description: 'Add your personal information', completed: false },
        { id: 'explore', title: 'Explore Products', description: 'Browse available products from verified sellers', completed: false },
        { id: 'orders', title: 'Understanding Orders', description: 'Learn how to track and manage your orders', completed: false },
      ];
    } else if (role === 'seller') {
      onboardingSteps = [
        { id: 'welcome', title: 'Welcome to SabiTradeNG', description: 'Let\'s set up your seller account', completed: false },
        { id: 'business', title: 'Business Information', description: 'Complete your business profile', completed: false },
        { id: 'products', title: 'Add Your First Product', description: 'List your first product for sale', completed: false },
        { id: 'dashboard', title: 'Seller Dashboard Tour', description: 'Learn about managing your business', completed: false },
      ];
    }

    setSteps(onboardingSteps);
  };

  const completeStep = (stepId: string) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, completed: true } : step
    ));
    
    const nextStepIndex = steps.findIndex(step => step.id === stepId) + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(nextStepIndex);
    } else {
      completeOnboarding();
    }
  };

  const completeOnboarding = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id);

      setIsOnboardingComplete(true);
      
      toast({
        title: 'Welcome to SabiTradeNG!',
        description: 'Your account setup is complete. Happy trading!',
      });
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  const skipOnboarding = async () => {
    await completeOnboarding();
  };

  return {
    currentStep,
    steps,
    isOnboardingComplete,
    completeStep,
    completeOnboarding,
    skipOnboarding,
    checkOnboardingStatus,
  };
}